import React, { useState } from 'react';
import { ArrowLeftIcon, MapPinIcon, PhoneIcon, StarIcon } from 'lucide-react';
import { CheckInDialog } from '../components/CheckInDialog';
import { PharmacyLocationPanel } from '../components/PharmacyLocationPanel';
import { PillarMatrix, ReviewPillars } from '../components/PillarMatrix';
import { northridgeReviews, pharmacies } from '../lib/platformData';
type ProfilePageProps = {
  pharmacyId: string;
  onBack: () => void;
  onClaim: () => void;
};
export function ProfilePage({
  pharmacyId,
  onBack,
  onClaim
}: ProfilePageProps) {
  const [checkInOpen, setCheckInOpen] = useState(false);
  const pharmacy = pharmacies.find(item => item.id === pharmacyId) ?? pharmacies[0];
  return <main className="min-h-[calc(100vh-4rem)] bg-[#F7F7F7] pb-28" data-id="element-356" data-mp="3kkjddxdjz53x">
      <div className="mx-auto w-full space-y-6 px-6 py-6 md:px-12 lg:px-16" data-id="element-357" data-mp="3kmcfczd8115l">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#555] hover:text-[#1C1C1C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A63A2B]" data-id="element-358" data-mp="1nqy7snek7lm3">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-359" data-mp="3ahzgrv8z523p" /> All results
        </button>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-360" data-mp="2wi7z9kj913ky">
          <div data-id="element-361" data-mp="2wk2hus71sjlv">
            <section className="rounded-lg border border-gray-100 bg-white p-5 sm:p-6" data-id="element-362" data-mp="31h1f3jm0d6oa">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between" data-id="element-363" data-mp="31ivxor9t4mp7">
                <div data-id="element-364" data-mp="3p8zsc950pc42">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#A63A2B]" data-id="element-365" data-mp="3u68a48m97e4s">
                    {pharmacy.category}
                  </p>
                  <h1 className="mt-2 text-3xl font-black tracking-tight text-[#1C1C1C]" data-id="element-366" data-mp="3fgkb5e1jork8">
                    {pharmacy.name}
                  </h1>
                  <p className="mt-3 flex items-center gap-1.5 text-sm text-[#666]" data-id="element-367" data-mp="bzlnyv2t4n1j">
                    <MapPinIcon className="h-4 w-4" data-id="element-368" data-mp="gqk51wvmdp5u" /> {pharmacy.address},
                    Chicago
                  </p>
                  <p className="mt-2 flex items-center gap-1.5 text-sm text-[#666]" data-id="element-369" data-mp="qlrbdwu3b61t">
                    <PhoneIcon className="h-4 w-4" data-id="element-370" data-mp="ln7qlzr8g0b1" /> (312) 555-0182
                  </p>
                </div>
                <div className="shrink-0" data-id="element-371" data-mp="gnuz31pf2524">
                  <PillarMatrix scores={pharmacy.scores} data-id="element-372" data-mp="gqpn137n280a" />
                </div>
              </div>
              <div className="mt-7 flex flex-wrap gap-3 border-t border-[#E5E5E5] pt-5" data-id="element-373" data-mp="cp434xwostgl">
                <button type="button" onClick={() => setCheckInOpen(true)} className="rounded-sm bg-[#A63A2B] px-4 py-3 text-sm font-bold text-white hover:bg-[#8D3024] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A63A2B] focus-visible:ring-offset-2" data-id="element-374" data-mp="7p9cmvgtq5w2">
                  Quick check in
                </button>
                <button type="button" className="rounded-sm border border-[#D5D5D5] px-4 py-3 text-sm font-bold text-[#1C1C1C] hover:bg-[#F7F7F7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A63A2B]" data-id="element-375" data-mp="3l21o1v2n7scd">
                  Save pharmacy
                </button>
              </div>
            </section>
            <PharmacyLocationPanel pharmacy={pharmacy} data-id="element-376" data-mp="24np0423cqsyg" />
            <section className="mt-8" data-id="element-377" data-mp="29l8gb7jm1ft1">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#A63A2B]" data-id="element-378" data-mp="29k74v1yomras">
                Real experiences, real people
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-[#1C1C1C]" data-id="element-379" data-mp="1gwjmzuosqeru">
                What your neighbors actually said
              </h2>
              <div className="mt-5 space-y-4" data-id="element-380" data-mp="1luctypxxbct6">
                {northridgeReviews.map(review => <article key={review.id} className="space-y-3 rounded-xl border border-gray-100 bg-white p-5 shadow-sm" data-id="element-381" data-mp="1qnxgnlu2gkd2">
                    <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-382" data-mp="y50jlmqv0ypa">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-383" data-mp="30u1w64kqeamy">
                        {review.reviewer}
                      </h3>
                      <ReviewPillars scores={review.scores} data-id="element-384" data-mp="2vrt0v1kdywho" />
                    </div>
                    <div className="flex flex-wrap gap-1.5 text-xs text-gray-400" data-id="element-385" data-mp="39mllgef4v2v4">
                      <span data-id="element-386" data-mp="34tsjfyb1u8mq">{pharmacy.name}</span>
                      <span data-id="element-387" data-mp="3jji0l2zjen8n">•</span>
                      <span data-id="element-388" data-mp="3jif8wsue4gro">{review.reason}</span>
                      <span data-id="element-389" data-mp="3jlb6uh68n5g4">•</span>
                      <span data-id="element-390" data-mp="3el6o0khuxymq">{review.timestamp}</span>
                    </div>
                    <p className="text-sm leading-relaxed text-gray-600" data-id="element-391" data-mp="3sjwli18la0bm">
                      {review.body}
                    </p>
                  </article>)}
              </div>
            </section>
          </div>
          <aside data-id="element-392" data-mp="1y76p9cur0f12">
            <section className="bg-[#008F82] p-6 text-white" data-id="element-393" data-mp="1y6nh2nuwh508">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#E7B158]" data-id="element-394" data-mp="1u5bqaf0aidqg">
                For pharmacy owners
              </p>
              <h2 className="mt-3 text-xl font-black leading-6" data-id="element-395" data-mp="1k1esd2cgnl0o">
                Your patients are already talking. The difference is we're
                keeping score.
              </h2>
              <p className="mt-4 text-sm leading-6 text-[#D7E2DB]" data-id="element-396" data-mp="1aaade2msc05u">
                Claim your pharmacy free to know exactly what's working and what
                to fix.
              </p>
              <button type="button" onClick={onClaim} className="mt-6 w-full rounded-sm bg-[#E7B158] px-4 py-3 text-sm font-black text-[#008F82] hover:bg-[#F0C475] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E7B158] focus-visible:ring-offset-2 focus-visible:ring-offset-[#008F82]" data-id="element-397" data-mp="10b2rgr1zix74">
                Claim your pharmacy free
              </button>
            </section>
            <section className="mt-4 rounded-lg border border-gray-100 bg-white p-5" data-id="element-398" data-mp="2qos5nz569jvq">
              <div className="flex items-center gap-1 text-[#B46423]" data-id="element-399" data-mp="2oaf055wdpr0t">
                {Array.from({
                length: 5
              }).map((_, index) => <StarIcon key={index} className="h-4 w-4 fill-current" data-id="element-400" data-mp="2k7iawwcc2wnw" data-mpi={index} />)}
              </div>
              <p className="mt-3 text-sm font-bold text-[#1C1C1C]" data-id="element-401" data-mp="2fbjsdpdos8ve">
                {pharmacy.reviewCount} structured community experiences
              </p>
              <p className="mt-1 text-xs leading-5 text-[#777]" data-id="element-402" data-mp="37xcoxh8e2r9q">
                Scores reflect recent reports on stock, wait, and care.
              </p>
            </section>
          </aside>
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[#E5E5E5] bg-white p-3 sm:hidden" data-id="element-403" data-mp="2y704rq4hl044">
        <button type="button" onClick={() => setCheckInOpen(true)} className="w-full bg-[#A63A2B] px-4 py-3 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A63A2B] focus-visible:ring-offset-2" data-id="element-404" data-mp="2t981y12aztl5">
          Are you here right now?{' '}
          <span className="underline underline-offset-2" data-id="element-405" data-mp="3tbob8hk5tfl3">
            Tap to update your neighbors.
          </span>
        </button>
      </div>
      <CheckInDialog open={checkInOpen} onClose={() => setCheckInOpen(false)} data-id="element-406" data-mp="71270gzsbhmd" />
    </main>;
}